import React, { useState } from 'react';
import { Button, Grid, Typography, Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Formik, Form } from 'formik';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import titleimg from "./img/logo-header-blu.png";
import { ReactComponent as DownloadIcon } from './img/download-icon.svg';



import './App.css';
import { useEffect } from 'react';

/**
 * FileUpload component handles the uploading and processing of files.
 * It provides functionality to upload multiple files, handle file changes,
 * submit the form, and display the uploaded files and processing results.
 *
 * @returns {JSX.Element} The FileUpload component.
 */
const FileUpload = () => {

  const { t } = useTranslation();

  const [rowsTable, setRowsTable] = useState([]);
  const [pending, setPending] = useState(false);
  const [downloadHref, setDownloadHref] = useState(null);

  const [fileState, setFileState] = useState({
    FIDIA: null,
    CUSTOMER: null,
    MATERIAL: null,
  });

  const [alertVisible, setAlertVisible] = useState(false);

  /**
   * Handles the download of multiple files as a zip archive.
   * @returns {Promise<void>} A promise that resolves when the download is complete.
   */
  const handleDownload = async () => {
    const zip = new JSZip();
    const urls = [
      "./FIDIA_XX.xlsx",
      "./CUSTOMER_XX.xlsx",
      "./MATERIAL_XX.xlsx"
    ];

    for (let url of urls) {
      const response = await fetch(url);
      const blob = await response.blob();
      const filename = url.split('/').pop();
      zip.file(filename, blob, { binary: true });
    }

    zip.generateAsync({ type: "blob" }).then(content => {
      saveAs(content, "Template.zip");
    });
  };

  useEffect(() => {
    if (alertVisible) {
      setTimeout(() => {
        setAlertVisible(false);
      }, 3000);
    }
  }, [alertVisible])

  /**
   * Handles the change of a file.
   *
   * @param {File} file - The file object to be updated.
   * @param {string} name - The name of the file.
   */
  const handleFileChange = (file, name) => {
    setFileState(prevState => ({ ...prevState, [name]: file }));
  };

  /**
   * Handles the form submission by uploading the files to the server.
   * 
   * @async
   * @function handleSubmit
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    const formData = new FormData();

    if (fileState['FIDIA'] == null && fileState['CUSTOMER'] == null && fileState['MATERIAL'] == null) {
      toast.error(t('allert.part1'));
      return;
    }

    Object.keys(fileState).forEach((key) => {
      formData.append(key, fileState[key]);
    });

    handleReset();


    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/upload-excel`, formData, {
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        timeout: 300000
      });

      if (response.status === 200) {
        toast.success(t('allert.part2'));
        setAlertVisible(true);
      }

    } catch (error) {
      let data;
      if (Object.hasOwnProperty.call(error, 'response')) {

        const files_errors = error.response.data.hasOwnProperty('files_errors') ? error.response.data.files_errors.join("; ") : [];
        if (error.response.data.hasOwnProperty('file_done') && error.response.data.file_done.length > 0) {
          toast.success(t('allert.part2') + error.response.data.file_done.join(", "));
        }
        switch (error.response.status) {
          case 500:
            let message_error = error.response.data.errors?.generic_error || "errors.generic";
            let error_occured = error.response.data.errors?.error || "";
            toast.error(t('allert.parts6') + ": " + t(message_error) + files_errors + "\n" + error_occured);
            break;

          case 409:
            data = error.response.data.response_JSON;
            let empty = true;

            Object.keys(data).forEach((key) => {
              if (Object.keys(data[key]).length !== 0) {
                empty = false;
              }
            });

            if (!empty) {
              toast.error(t('allert.part3'));
              handleData(data);
              if (data.meta?.downloadUrl) {
                const downloadLink = `${process.env.REACT_APP_BASE_URL}${data.meta.downloadUrl}`;
                setDownloadHref(downloadLink); // lo metti nello stato e poi lo mostri come <a>
              }
            }
            else {
              toast.success(t('allert.part2'));
              setAlertVisible(true);
            }
            break;

          case 400:
            data = error.response.data;
            toast.error(t('allert.part3'));
            handleData(data.errors);
            if (data.meta?.downloadUrl) {
              const downloadLink = `${process.env.REACT_APP_BASE_URL}${data.meta.downloadUrl}`;
              setDownloadHref(downloadLink); // lo metti nello stato e poi lo mostri come <a>
            }

            break;

          case 404:
            toast.error(t('allert.parts6') + "The service/api is not available, check if the URL is correct.");
            break;

          default:
            toast.error("ERROR: " + error.response.status);
            console.error(error);
            break
        }

      }
      else if (error.hasOwnProperty('name') && error.name === "AxiosError" && error.code === "ERR_NETWORK") {
        toast.error(t('allert.parts6') + ": " + t('allert.networkError'));
      }
      else {

        toast.error(t('allert.parts6') + ": " + t(error.message));
      }

    } finally {
      setPending(false);
    }
  };

  /**
   * Handles the data and updates the rows table.
   * @param {Object} data - The data to be processed.
   */
  function handleData(data) {

    for (const keyNameFile in data) {
      for (const keyCellAdress in data[keyNameFile]) {
        if (typeof data[keyNameFile][keyCellAdress] === "string") {
          if (data[keyNameFile][keyCellAdress].includes("Expected value:") && data[keyNameFile][keyCellAdress].includes("Actual value:")) {
            const expectedValue = data[keyNameFile][keyCellAdress].split("|")[0].split("Expected value: ")[1];
            const actualValue = data[keyNameFile][keyCellAdress].split("|")[1].split("Actual value: ")[1];
            setRowsTable(prevRowsTable => [...prevRowsTable, { fileName: keyNameFile, cell: keyCellAdress, expectedValue: expectedValue, actualValue: actualValue }]);
          }
          else if (data[keyNameFile][keyCellAdress].includes("This column name")) {
            const actualValue = data[keyNameFile][keyCellAdress].split("This column name: ")[1];
            setRowsTable(prevRowsTable => [...prevRowsTable, { fileName: keyNameFile, cell: keyCellAdress, actualValue: actualValue, expectedValue: t('errors.header error') + keyNameFile }]);
          }
        }
      }
    }
  }


  /**
   * Resets the rows table.
   */
  const handleReset = () => {
    setRowsTable([]);
  };

  /**
   * Deletes a key from the file state.
   *
   * @param {string} keyName - The name of the key to delete.
   */
  const handleDelete = (keyName) => {
    setFileState(prevState => {
      const newState = { ...prevState };
      delete newState[keyName];
      return newState;
    });
    setRowsTable([])
    setDownloadHref(null);
  };

  /**
   * Function to handle file drop event.
   *
   * @param {Array<File>} acceptedFiles - Array of accepted files.
   * @param {Array<File>} fileRejections - Array of rejected files.
   */

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: '.xls,.xlsx',

    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 3) {
        toast.error(t('allert.part4'));  //
        return;
      }

      acceptedFiles.forEach((acceptedFile) => {

        const nameFile = acceptedFile.name.toUpperCase();

        var regex = new RegExp("(FIDIA|CUSTOMER|MATERIAL)_([A-Z]{2})(.XLSX|.XLS)");

        if (!regex.test(nameFile)) {
          toast.error(t('allert.part5'));
          return;
        }

        let field = acceptedFile.name.split('_')[0].toUpperCase();


        handleFileChange(acceptedFile, field);

      });

    },
    maxFiles: 3
  });

  document.body.style.color = "#000000";
  return (
    <div className="App" style={{ padding: 20 }}>
      <Toaster
        position="top-right"
        reverseOrder={false}
      />
      <Formik
        initialValues={{ FIDIA: '', CUSTOMER: '', MATERIAL: '' }}
        onSubmit={() => {
          setPending(true)
          handleSubmit()
        }}
      >
        <Form>
          <Grid item style={{ padding: 20 }}>
            <img src={titleimg} alt="Title" style={{ display: 'block', margin: '0 auto', height: 110, width: 340 }} />
          </Grid>
          <Box component="section" sx={{ background: '#F1F2F6', borderRadius: 2 }}>
            <div style={{ padding: 20 }}>
              <Typography variant='body1'>
                {t('description.part3')}
              </Typography>

              <Typography variant='body1'>
                {t('description.part4')}

                <Link href={t('link')} underline="hover" target="_blank"><strong>{t('description.part5')}</strong></Link>
                {t('description.part6')}
              </Typography>

              <Typography variant='body1'>
                <Link onClick={handleDownload} underline="hover" ><strong>{t('description.part1')}</strong></Link> {t('description.part2')}
              </Typography>

              <Typography variant='body1'>
                {t('description.part7')}
              </Typography>

            </div>

          </Box>

          <Grid container className="container">
            <div className='zone' {...getRootProps()}>
              <input {...getInputProps()} />
              {isDragActive ? (
                <div className="drop-files">
                  <Grid item className='element'>
                    {t('dropfiles')}
                  </Grid>
                </div>
              ) : (
                <div className="drag-files">
                  <Grid item className='element'>
                    <Typography>
                      {t('dragfiles.part1')} <strong>{t('dragfiles.part3')}</strong> {t('dragfiles.part2')}
                    </Typography>
                  </Grid>
                </div>
              )}
            </div>
          </Grid>

          {Object.keys(fileState).some(keyName => fileState[keyName]) && (
            <div className="containerItems">
              <h2>{t('istelemntTitle')}</h2>
              <ul>
                {Object.keys(fileState).map((keyName, i) => (
                  fileState[keyName] && <li key={i}>
                    <span>{fileState[keyName].name}</span>
                    <Button variant="outlined" color="error" onClick={() => handleDelete(keyName)}>{t('delete')}</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Grid container justifyContent="center" style={{ padding: '20px' }}>
            {alertVisible ? (
              <Alert severity="success">{t('alertSuccess')}</Alert>
            ) : (
              <>
                <Button type="submit" variant="contained" disabled={pending || !Object.keys(fileState).some(keyName => fileState[keyName])} style={{ backgroundColor: '#606EA8' }}>
                  {t('Submit')}
                </Button>
                {pending && <CircularProgress style={{ color: '#606EA8' }} />}
              </>

            )}
          </Grid>
        </Form>
      </Formik>
      {rowsTable.length !== 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding={'20px'}
        >
          <div style={{ display: 'grid' }}>
            <Typography
              sx={{
                textAlign: 'center',
                padding: "20px",
                color: "#00000",
                fontFamily: 'Arial',
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '10px',
              }}
              variant="h5"
            >
              <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                <strong>{t('tableTitle')}</strong>

                {downloadHref && (
                  <Tooltip title="Download the Excel file with all errors">
                    <Link href={downloadHref} target="_blank" download underline="none">
                      <IconButton>
                        <DownloadIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
              </Grid>
            </Typography>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650, maxWidth: 1000 }} aria-label="simple table">
                <TableHead sx={{ background: '#606EA8' }}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'Arial', fontSize: '18px', color: 'white' }}><strong>{t('File-Name')}</strong></TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'Arial', fontSize: '18px', color: 'white' }}><strong>{t('Cell')}</strong></TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'Arial', fontSize: '18px', color: 'white' }}><strong>{t('Expected-Value')}</strong></TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'Arial', fontSize: '18px', color: 'white' }}><strong>{t('Actual-Value')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsTable.map((row, index) => (
                    <TableRow
                      key={`row.fileName-${index}`}
                      sx={{ backgroundColor: index % 2 === 0 ? '#F5F9FF' : '#E8EAF6', padding: '10px 0' }}
                    >
                      <TableCell component="th" scope="row">
                        {row.fileName}
                      </TableCell>
                      <TableCell align="center">{row.cell}</TableCell>
                      <TableCell align="center">{row.expectedValue}</TableCell>
                      <TableCell align="center" style={{ maxWidth: '300px', overflow: 'auto', whiteSpace: 'nowrap' }}>{row.actualValue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      ) : (
        <br>
        </br>
      )}
    </div>
  );
}

export default FileUpload;
